import React, { useMemo, useState } from 'react';
import { message, Select, Tag, Text, theme } from 'components';
import styled from 'styled-components';
import { Loading } from '@app/react-common';
import { debounce } from 'lodash';
import { SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import { PopulatedUser } from 'api/UserController/getProfile';
import { searchForMLSStaff } from 'api/UserController';
import type { IUserSearchForMLSStaffQuery } from '@/app-package';

type ShackleSearchBarProps = {
  visible?: boolean;
  user?: PopulatedUser;
  shackleUserIds: string[] | undefined;
  onShackleUserIdsChange: (userIds: string[] | undefined) => void;
  isCrossOrgSearch: boolean;
};

type UserData = { id: string; firstName: string; lastName: string };

const ShackleAssigneeSearchBar: React.FC<ShackleSearchBarProps> = ({
  visible,
  user,
  shackleUserIds,
  onShackleUserIdsChange,
  isCrossOrgSearch,
}) => {
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [userData, setUserData] = useState<UserData[]>([]);

  const getUserDataByBrokerage = async (searchText: string) => {
    if (!user || !user?.org) {
      return;
    }

    setLoading(true);
    try {
      const SEARCH_QUERY: IUserSearchForMLSStaffQuery = {
        name: searchText,
        ...(!isCrossOrgSearch && { orgId: user.org.id }),
        supraSubscriptionStatuses: [SupraSystemSubscriptionStatus.subscribed],
        eligibleGranteesOnly: true,
        page: 0,
        size: 10,
      };

      const result = await searchForMLSStaff(SEARCH_QUERY);
      setUserData(
        result.users
          .filter(entry => entry.id !== user?.id)
          .map(u => ({ id: u.id, firstName: u.firstName, lastName: u.lastName })),
      );
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onSearch = async (searchTerm: string) => {
    setUserData([]);
    setQuery(searchTerm);
    if (searchTerm === '') return;
    await getUserDataByBrokerage(searchTerm);
  };

  const debounceHandleSearch = useMemo(() => debounce(onSearch, 300), []);

  if (visible) {
    return (
      <>
        <StyledSelectContainer>
          <StyledSelect
            showSearch
            optionLabelProp="label"
            placeholder="Search Agent"
            defaultActiveFirstOption={false}
            showArrow={false}
            filterOption={false}
            allowClear
            mode="multiple"
            value={shackleUserIds}
            onSearch={debounceHandleSearch}
            onChange={onShackleUserIdsChange}
            notFoundContent={
              loading ? (
                <Loading />
              ) : (
                <StyledNoResultContainer>
                  {query === '' ? (
                    <Text type="secondary">Search Agents by name</Text>
                  ) : (
                    <Text type="secondary">No Agents found.</Text>
                  )}
                </StyledNoResultContainer>
              )
            }
            options={userData?.map(d => ({
              value: d?.id,
              label: `${d?.firstName} ${d?.lastName}`,
            }))}
            tagRender={prop => (
              <StyledTag color="gray" closable>
                {prop.label}
              </StyledTag>
            )}
          />
        </StyledSelectContainer>
      </>
    );
  }

  return null;
};

const StyledSelectContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  margin-top: 10px;
  background-color: #fff;
`;

const StyledSelect = styled(Select)`
  min-width: 100%;
  display: flex;
  position: relative;
  .ant-select-selection-item {
    padding: 0 0px !important;
  }
  .ant-select-selection-search {
    padding: 0px 0px !important;
  }
  .ant-select-selection-placeholder {
    padding: 0px 0px !important;
  }
`;

const StyledNoResultContainer = styled.div`
  text-align: center;
  margin: 20px 0px;
`;

const StyledTag = styled(Tag)`
  && {
    color: ${theme.gray8};
    border: 1px solid ${theme.gray6};
    border-radius: 0px;
  }
`;

export default ShackleAssigneeSearchBar;
